import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { Card, SectionLabel, Button, Input, Select } from "../components/ui";
import TeamCard from "../components/TeamCard";
import BillingCard from "../components/BillingCard";
import AiProviderCard from "../components/AiProviderCard";
import ResetWorkspaceCard from "../components/ResetWorkspaceCard";
import AccountDataCard from "../components/AccountDataCard";
import api from "../lib/api";
import { cn } from "../lib/utils";
import { Settings as SettingsIcon, Sun, Moon, Globe, Mail, Send, Sparkles, Clock, Bell, AtSign } from "lucide-react";

export default function Settings() {
  const { t, theme, setTheme, lang, setLang, user, toast } = useApp();
  const [digest, setDigest] = useState({ frequency: "off", include_ai: true, email_configured: true, send_hour: 8 });
  const [notif, setNotif] = useState({ notif_inapp: true, notif_email: false });
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => {
    api.get("/digest/preferences").then(({ data }) => setDigest(data)).catch(() => {});
    api.get("/notifications/preferences").then(({ data }) => setNotif(data)).catch(() => {});
  }, []);

  const saveNotif = async (patch) => {
    const next = { ...notif, ...patch };
    setNotif(next);
    try { await api.put("/notifications/preferences", next); toast(t("notif_saved")); }
    catch { toast(t("digest_error")); setNotif(notif); }
  };

  const [pfCurrency, setPfCurrency] = useState("EUR");
  const [pfCurrencies, setPfCurrencies] = useState(["EUR", "USD", "GBP", "CHF", "CAD", "JPY"]);
  useEffect(() => {
    api.get("/portfolio/settings").then(({ data }) => {
      setPfCurrency(data.portfolio_currency);
      if (data.currencies) setPfCurrencies(data.currencies);
    }).catch(() => {});
  }, []);

  const savePfCurrency = async (cur) => {
    setPfCurrency(cur);
    try {
      await api.put("/portfolio/settings", { currency: cur });
      toast(t("portfolio_currency_saved"));
    } catch {
      toast(t("digest_error"));
    }
  };

  useEffect(() => {
    if (user?.email) setTestEmail((v) => v || user.email);
  }, [user?.email]);

  const savePrefs = async (patch) => {
    setDigest((d) => ({ ...d, ...patch }));
    try {
      const { data } = await api.put("/digest/preferences", { ...patch, origin: window.location.origin });
      setDigest(data);
      toast(t("digest_saved"));
    } catch {
      toast(t("digest_error"));
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/digest/send", {
        lang, include_ai: digest.include_ai, origin: window.location.origin,
        to: testEmail || undefined,
      });
      toast(
        data.sent
          ? `${t("digest_sent")} ${data.email}`
          : data.email_configured
            ? t("digest_send_failed")
            : t("digest_preview_ready")
      );
    } catch {
      toast(t("digest_error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-7">
        <SettingsIcon className="w-6 h-6 text-text-secondary" />
        <h1 className="text-3xl font-heading font-medium tracking-tight">{t("settings_title")}</h1>
      </div>

      <BillingCard />

      <AiProviderCard />

      <Card className="mb-6">
        <SectionLabel>{t("theme")}</SectionLabel>
        <div className="flex gap-3 mt-3">
          {[
            { val: "dark", icon: Moon, label: t("dark") },
            { val: "light", icon: Sun, label: t("light") },
          ].map((o) => (
            <button
              key={o.val}
              onClick={() => setTheme(o.val)}
              data-testid={`set-theme-${o.val}`}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition-all",
                theme === o.val ? "border-current" : "border-[var(--border)] text-text-secondary hover:text-text-primary"
              )}
              style={theme === o.val ? { borderColor: "#2563EB", color: "var(--text-primary)" } : {}}
            >
              <o.icon className="w-4 h-4" /> {o.label}
            </button>
          ))}
        </div>
      </Card>

      <Card className="mb-6">
        <SectionLabel>{t("language")}</SectionLabel>
        <div className="flex gap-3 mt-3">
          {[
            { val: "fr", label: "Français" },
            { val: "en", label: "English" },
          ].map((o) => (
            <button
              key={o.val}
              onClick={() => setLang(o.val)}
              data-testid={`set-lang-${o.val}`}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition-all",
                lang === o.val ? "border-current" : "border-[var(--border)] text-text-secondary hover:text-text-primary"
              )}
              style={lang === o.val ? { borderColor: "#2563EB", color: "var(--text-primary)" } : {}}
            >
              <Globe className="w-4 h-4" /> {o.label}
            </button>
          ))}
        </div>
      </Card>

      <Card className="mb-6" data-testid="portfolio-currency-card">
        <SectionLabel>{t("settings_pf_currency")}</SectionLabel>
        <p className="text-sm text-text-secondary mt-1 mb-3">{t("settings_pf_currency_desc")}</p>
        <div className="flex flex-wrap gap-2">
          {pfCurrencies.map((c) => (
            <button
              key={c}
              onClick={() => savePfCurrency(c)}
              data-testid={`set-pf-currency-${c}`}
              className={cn(
                "px-4 py-2 rounded-xl border text-sm font-medium transition-all",
                pfCurrency === c ? "border-current" : "border-[var(--border)] text-text-secondary hover:text-text-primary"
              )}
              style={pfCurrency === c ? { borderColor: "#2563EB", color: "var(--text-primary)" } : {}}
            >
              {c}
            </button>
          ))}
        </div>
      </Card>

      {/* Team / workspace members */}
      <TeamCard />

      {/* Notifications */}
      <Card className="mb-6" data-testid="notif-card">
        <div className="flex items-center gap-2">
          <AtSign className="w-4 h-4 text-text-secondary" />
          <SectionLabel className="!mb-0">{t("notif_title")}</SectionLabel>
        </div>
        <p className="text-text-secondary text-sm mt-1.5 mb-4">{t("notif_subtitle")}</p>
        {[
          { key: "notif_inapp", label: t("notif_inapp"), Icon: Bell },
          { key: "notif_email", label: t("notif_email"), Icon: Mail },
        ].map(({ key, label, Icon }) => (
          <button key={key} onClick={() => saveNotif({ [key]: !notif[key] })} role="switch" aria-checked={notif[key]}
            data-testid={`notif-${key === "notif_inapp" ? "inapp" : "email"}`}
            className="flex items-center justify-between w-full mt-2 px-3.5 py-3 rounded-xl border border-[var(--border)] text-sm hover:bg-surface-hover transition-colors">
            <span className="flex items-center gap-2 text-text-primary"><Icon className="w-4 h-4 text-text-secondary" /> {label}</span>
            <span className="relative w-9 h-5 rounded-full transition-colors shrink-0" style={{ backgroundColor: notif[key] ? "#2563EB" : "var(--border)" }}>
              <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: notif[key] ? "18px" : "2px" }} />
            </span>
          </button>
        ))}
      </Card>
      {/* Digest email */}
      <Card className="mb-6" data-testid="digest-card">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-text-secondary" />
          <SectionLabel className="!mb-0">{t("digest_title")}</SectionLabel>
        </div>
        <p className="text-text-secondary text-sm mt-1.5 mb-4">{t("digest_subtitle")}</p>

        <div className="flex gap-3">
          {[
            { val: "off", label: t("digest_off") },
            { val: "daily", label: t("digest_daily") },
            { val: "weekly", label: t("digest_weekly") },
          ].map((o) => (
            <button
              key={o.val}
              onClick={() => savePrefs({ frequency: o.val })}
              data-testid={`digest-freq-${o.val}`}
              className={cn(
                "flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all",
                digest.frequency === o.val ? "border-current" : "border-[var(--border)] text-text-secondary hover:text-text-primary"
              )}
              style={digest.frequency === o.val ? { borderColor: "#2563EB", color: "var(--text-primary)" } : {}}
            >
              {o.label}
            </button>
          ))}
        </div>

        {digest.frequency !== "off" && (
          <div className="flex items-center gap-3 mt-3" data-testid="digest-send-time-row">
            <span className="flex items-center gap-2 text-sm text-text-secondary shrink-0">
              <Clock className="w-4 h-4" /> {t("digest_send_time")}
            </span>
            <Select
              value={digest.send_hour ?? 8}
              onChange={(e) => savePrefs({ send_hour: Number(e.target.value), tz_offset: new Date().getTimezoneOffset() })}
              data-testid="digest-send-hour"
              className="max-w-[140px] ml-auto"
            >
              {Array.from({ length: 24 }, (_, hr) => (
                <option key={hr} value={hr}>{String(hr).padStart(2, "0")}:00</option>
              ))}
            </Select>
          </div>
        )}

        <button
          onClick={() => savePrefs({ include_ai: !digest.include_ai })}
          role="switch"
          aria-checked={digest.include_ai}
          data-testid="digest-include-ai"
          className="flex items-center justify-between w-full mt-3 px-3.5 py-3 rounded-xl border border-[var(--border)] text-sm hover:bg-surface-hover transition-colors"
        >
          <span className="flex items-center gap-2 text-text-primary">
            <Sparkles className="w-4 h-4 text-text-secondary" /> {t("digest_include_ai")}
          </span>
          <span
            className="relative w-9 h-5 rounded-full transition-colors shrink-0"
            style={{ backgroundColor: digest.include_ai ? "#2563EB" : "var(--border)" }}
          >
            <span
              className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
              style={{ left: digest.include_ai ? "18px" : "2px" }}
            />
          </span>
        </button>

        <div className="mt-4">
          <label className="text-xs text-text-secondary block mb-1.5">{t("digest_test_label")}</label>
          <div className="flex items-center gap-3">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="digest-test-email"
              className="flex-1"
            />
            <Button onClick={sendNow} disabled={sending || !testEmail} data-testid="digest-send-now" className="shrink-0">
              <Send className="w-4 h-4 mr-2" /> {sending ? t("digest_sending") : t("digest_send_now")}
            </Button>
          </div>
          {!digest.email_configured && (
            <p className="text-[11px] text-text-secondary mt-2">{t("digest_not_configured")}</p>
          )}
        </div>
      </Card>

      <Card>
        <SectionLabel>{t("account")}</SectionLabel>
        <div className="flex items-center gap-3 mt-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-surface-hover flex items-center justify-center font-semibold">
              {(user?.name || "?").slice(0, 1)}
            </div>
          )}
          <div>
            <div className="font-medium">{user?.name}</div>
            <div className="text-sm text-text-secondary">{user?.email}</div>
          </div>
        </div>
      </Card>

      <AccountDataCard />

      <ResetWorkspaceCard />
    </div>
  );
}
